import { MoreOutlined, PlusOutlined } from '@ant-design/icons'
import { Modal, Popover } from 'antd'
import { AxiosError } from 'axios'
import classNames from 'classnames'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { ZodError } from 'zod'

import AtomButton from '@/components/Atoms/AtomButton/AtomButton'
import AtomSearchInput from '@/components/Atoms/AtomSearchInput/AtomSearchInput'
import AtomSelect from '@/components/Atoms/AtomSelect/AtomSelect'
import AtomSwitch from '@/components/Atoms/AtomSwitch/AtomSwitch'
import HoverElement from '@/components/Atoms/HoverElement/HoverElement'
import Loader from '@/components/Atoms/Loader/Loader'
import CharacteristicCreationModal from '@/components/EntityCreationModals/CharacteristicCreationModal/CharacteristicCreationModal'
import TreeEntityCreationModal from '@/components/EntityCreationModals/TreeEntityCreationModal/TreeEntityCreationModal'
import { GENERAL_ERRORS_MESSAGES, NULL_UUID_ID, TYPE_LABELS } from '@/constants'
import { categorySchema, categoryWithoutChildsSchema } from '@/schemas'
import { useTreeTilesStore } from '@/stores/feed/treeTilesStore'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type {
  AntdTreeType,
  CategoryEntityModel,
  CategoryInterface,
  CharacteristicValueType,
  UnitOfMeasurementInterface,
} from '@/types'
import api from '@/utils/api/api'
import getGeneralErrorMessage from '@/utils/api/errors/getGeneralErrorMessage'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import {
  getStatusBadge,
  isCatalogApprover,
} from '@/utils/catalog/approvalStatus'
import { cleanCategoryTree } from '@/utils/catalog/cleanDescription'
import convertToAntdFormatTree from '@/utils/tree/antd/convertToAntdFormatTree'

import CategoryDetailsModal from '../shared/CategoryDetailsModal/CategoryDetailsModal'
import CharacteristicDetailsModal from '../shared/CharacteristicDetailsModal/CharacteristicDetailsModal'
import cn from './CatalogApproval.module.scss'
import CatalogApprovalTable, {
  type CatalogApprovalColumn,
} from './CatalogApprovalTable/CatalogApprovalTable'
import useCategoryModerationTable from './CategoryModerationTreeTable/CategoryModerationTreeTable'
import StatusChip from './StatusChip/StatusChip'

type CharacteristicRecord = {
  id: string
  code: number
  description: string
  status: 'DRAFT' | 'APPROVED' | 'REJECTED'
  valueType: CharacteristicValueType
  useUnitOfMeasurement: boolean
  unitOfMeasurementDto?: UnitOfMeasurementInterface | null
}

type ModerationSection = 'categories' | 'characteristics'
type StatusFilter = 'approved' | 'moderation'
type CategoryRecord = CategoryEntityModel

const SECTION_OPTIONS: { value: ModerationSection; label: string }[] = [
  { value: 'characteristics', label: 'Характеристики' },
  { value: 'categories', label: 'Категории' },
]

const SECTION_PATHS: Record<ModerationSection, string> = {
  characteristics: '/catalog-approval/characteristics',
  categories: '/catalog-approval/categories',
}

const STATUS_FILTER_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'approved', label: 'Утверждённые' },
  { value: 'moderation', label: 'На модерации' },
]

const isStatusFilter = (value: unknown): value is StatusFilter =>
  STATUS_FILTER_OPTIONS.some((option) => option.value === value)

const CHARACTERISTIC_STATUS_ORDER: Record<
  CharacteristicRecord['status'],
  number
> = {
  DRAFT: 0,
  REJECTED: 1,
  APPROVED: 2,
}

const normalizeCategoryRecord = (
  item: Partial<CategoryRecord> & { parent?: { id?: string | null } }
): CategoryRecord => ({
  id: item.id || '',
  parentId: item.parentId ?? item.parent?.id ?? NULL_UUID_ID,
  code: item.code || 0,
  description: item.description || '',
  priority: item.priority,
  status: item.status || 'DRAFT',
})

const sortCharacteristics = (items: CharacteristicRecord[]) =>
  [...items].sort((a, b) => {
    const byStatus =
      CHARACTERISTIC_STATUS_ORDER[a.status] -
      CHARACTERISTIC_STATUS_ORDER[b.status]

    if (byStatus !== 0) return byStatus

    return a.code - b.code
  })

const detachCategoryNode = (
  nodes: CategoryInterface[],
  categoryId: string
): {
  detachedNode: CategoryInterface | null
  nextTree: CategoryInterface[]
} => {
  let detachedNode: CategoryInterface | null = null

  const nextTree = nodes.reduce<CategoryInterface[]>((accumulator, node) => {
    if (node.category.id === categoryId) {
      detachedNode = node
      return accumulator
    }

    const detachedFromChildren = detachCategoryNode(
      node.childCategories || [],
      categoryId
    )

    if (detachedFromChildren.detachedNode) {
      detachedNode = detachedFromChildren.detachedNode
      accumulator.push({
        ...node,
        childCategories: detachedFromChildren.nextTree,
      })
      return accumulator
    }

    accumulator.push(node)
    return accumulator
  }, [])

  return { detachedNode, nextTree }
}

const attachCategoryNode = (
  nodes: CategoryInterface[],
  parentId: string,
  nodeToAttach: CategoryInterface
): { attached: boolean; nextTree: CategoryInterface[] } => {
  if (parentId === NULL_UUID_ID) {
    return {
      attached: true,
      nextTree: [...nodes, nodeToAttach],
    }
  }

  let attached = false

  const nextTree = nodes.map((node) => {
    if (node.category.id === parentId) {
      attached = true
      return {
        ...node,
        childCategories: [...(node.childCategories || []), nodeToAttach],
      }
    }

    const attachedToChildren = attachCategoryNode(
      node.childCategories || [],
      parentId,
      nodeToAttach
    )

    if (attachedToChildren.attached) {
      attached = true
      return {
        ...node,
        childCategories: attachedToChildren.nextTree,
      }
    }

    return node
  })

  return { attached, nextTree }
}

const applyCategoryUpdateToTree = (
  nodes: CategoryInterface[],
  updatedCategory: CategoryRecord
) => {
  const { detachedNode, nextTree } = detachCategoryNode(
    nodes,
    updatedCategory.id
  )
  const nextNode: CategoryInterface = detachedNode
    ? {
        ...detachedNode,
        category: {
          ...detachedNode.category,
          ...updatedCategory,
        },
      }
    : {
        category: updatedCategory,
        childCategories: [],
      }

  const attachedNode: CategoryInterface = {
    ...nextNode,
    category: {
      ...nextNode.category,
      parentId: updatedCategory.parentId,
    },
  }

  const attachedToTree = attachCategoryNode(
    nextTree,
    updatedCategory.parentId,
    attachedNode
  )

  return attachedToTree.attached
    ? attachedToTree.nextTree
    : [...attachedToTree.nextTree, attachedNode]
}

function CatalogApproval() {
  const [openCreateCategoryModal, setOpenCreateCategoryModal] = useState(false)
  const [openCreateCharacteristicModal, setOpenCreateCharacteristicModal] =
    useState(false)
  const [loadingError, setLoadingError] = useState('')
  const [loading, setLoading] = useState(false)
  const [pendingActionKey, setPendingActionKey] = useState('')
  const [newCategories, setNewCategories] = useState<CategoryRecord[]>([])
  const [allCharacteristics, setAllCharacteristics] = useState<
    CharacteristicRecord[]
  >([])
  const [newCharacteristics, setNewCharacteristics] = useState<
    CharacteristicRecord[]
  >([])
  const [selectedCategory, setSelectedCategory] =
    useState<CategoryRecord | null>(null)
  const [selectedCharacteristic, setSelectedCharacteristic] =
    useState<CharacteristicRecord | null>(null)
  const [categoryDetailsOpen, setCategoryDetailsOpen] = useState(false)
  const [characteristicDetailsOpen, setCharacteristicDetailsOpen] =
    useState(false)
  const [antdCategoriesTree, setAntdCategoriesTree] = useState<AntdTreeType[]>(
    []
  )
  const [categoryTab, setCategoryTab] = useState<StatusFilter>('approved')
  const [characteristicTab, setCharacteristicTab] =
    useState<StatusFilter>('approved')
  const [searchQuery, setSearchQuery] = useState('')
  const [modal, contextHolder] = Modal.useModal()
  const [loadedState, setLoadedState] = useState({
    categoriesAll: false,
    categoriesNew: false,
    characteristicsAll: false,
    characteristicsNew: false,
  })

  const requestPromisesRef = useRef<
    Partial<
      Record<
        | 'categoriesAll'
        | 'categoriesNew'
        | 'characteristicsAll'
        | 'characteristicsNew',
        Promise<void>
      >
    >
  >({})
  const pendingLoadsRef = useRef(0)

  const navigate = useNavigate()
  const location = useLocation()
  const currentUser = useUserInfoStore((state) => state.currentUser)
  const isOrganisationSelected = useUserInfoStore(
    (state) => Object.keys(state.selectedOrganisation).length > 0
  )
  const actionPopoverClassName = classNames(
    cn.actionPopover,
    isOrganisationSelected && cn.organisationSelected
  )
  const isApprover = isCatalogApprover(currentUser?.roles)

  const { categoriesTree: tree, setCategoriesTree: setTree } =
    useTreeTilesStore((state) => state)

  const activeSection: ModerationSection = location.pathname.includes(
    '/categories'
  )
    ? 'categories'
    : 'characteristics'

  const isActiveSectionReady =
    activeSection === 'categories'
      ? loadedState.categoriesAll && loadedState.categoriesNew
      : loadedState.characteristicsAll &&
        (characteristicTab !== 'moderation' || loadedState.characteristicsNew)

  // Existing data loaders are intentionally reused here without wrapping in callbacks.

  useEffect(() => {
    if (!isApprover) return

    if (location.pathname === '/catalog-approval') {
      navigate('/catalog-approval/characteristics', { replace: true })
    }
  }, [isApprover, location.pathname, navigate])

  const beginLoad = () => {
    pendingLoadsRef.current += 1
    setLoading(true)
    setLoadingError('')
  }

  const endLoad = () => {
    pendingLoadsRef.current = Math.max(0, pendingLoadsRef.current - 1)
    setLoading(pendingLoadsRef.current > 0)
  }

  const handleLoadError = (error: unknown) => {
    if (error instanceof AxiosError) {
      setLoadingError('')
      handleApiError(error, modal)
    } else if (error instanceof ZodError) {
      setLoadingError(GENERAL_ERRORS_MESSAGES.ZOD_ERROR)
    } else {
      setLoadingError(getGeneralErrorMessage(error))
    }
  }

  const loadCharacteristicsAll = async (force = false, silent = false) => {
    if (!force && loadedState.characteristicsAll) return
    const existingPromise = requestPromisesRef.current.characteristicsAll
    if (existingPromise) return existingPromise

    if (silent) setLoadingError('')
    else beginLoad()
    const promise = (async () => {
      try {
        const response = await api.get('/main/characteristics')
        setAllCharacteristics(
          sortCharacteristics(response.data.characteristics ?? [])
        )
        setLoadedState((current) => ({ ...current, characteristicsAll: true }))
      } catch (error) {
        handleLoadError(error)
      } finally {
        requestPromisesRef.current.characteristicsAll = undefined
        if (!silent) endLoad()
      }
    })()

    requestPromisesRef.current.characteristicsAll = promise
    return promise
  }

  const loadCharacteristicsNew = async (force = false, silent = false) => {
    if (!force && loadedState.characteristicsNew) return
    const existingPromise = requestPromisesRef.current.characteristicsNew
    if (existingPromise) return existingPromise

    if (silent) setLoadingError('')
    else beginLoad()
    const promise = (async () => {
      try {
        const response = await api.get('/main/characteristics/moderation')
        setNewCharacteristics(response.data.characteristics ?? [])
        setLoadedState((current) => ({ ...current, characteristicsNew: true }))
      } catch (error) {
        handleLoadError(error)
      } finally {
        requestPromisesRef.current.characteristicsNew = undefined
        if (!silent) endLoad()
      }
    })()

    requestPromisesRef.current.characteristicsNew = promise
    return promise
  }

  const loadCategoriesAll = async (force = false, silent = false) => {
    if (!force && loadedState.categoriesAll) return
    const existingPromise = requestPromisesRef.current.categoriesAll
    if (existingPromise) return existingPromise

    if (silent) setLoadingError('')
    else beginLoad()
    const promise = (async () => {
      try {
        const response = await api.get('/categories/root-trees')
        const treeData = response.data.trees || []
        const validatedTree = categorySchema.array().parse(treeData)
        const cleanedTree = cleanCategoryTree(validatedTree)

        setTree(cleanedTree)
        setAntdCategoriesTree(
          convertToAntdFormatTree({
            nodes: cleanedTree,
            classNames: cn,
            allSelectable: true,
            inTreeSelect: true,
            respectApprovalStatus: false,
          })
        )
        setLoadedState((current) => ({ ...current, categoriesAll: true }))
      } catch (error) {
        handleLoadError(error)
      } finally {
        requestPromisesRef.current.categoriesAll = undefined
        if (!silent) endLoad()
      }
    })()

    requestPromisesRef.current.categoriesAll = promise
    return promise
  }

  const loadCategoriesNew = async (force = false, silent = false) => {
    if (!force && loadedState.categoriesNew) return
    const existingPromise = requestPromisesRef.current.categoriesNew
    if (existingPromise) return existingPromise

    if (silent) setLoadingError('')
    else beginLoad()
    const promise = (async () => {
      try {
        const response = await api.get('/categories/moderation')
        const moderationCategoriesRaw = response.data.categories || []
        const normalizedModerationCategories = moderationCategoriesRaw.map(
          normalizeCategoryRecord
        )

        setNewCategories(
          categoryWithoutChildsSchema
            .array()
            .parse(normalizedModerationCategories)
            .sort((a, b) => a.code - b.code)
        )
        setLoadedState((current) => ({ ...current, categoriesNew: true }))
      } catch (error) {
        handleLoadError(error)
      } finally {
        requestPromisesRef.current.categoriesNew = undefined
        if (!silent) endLoad()
      }
    })()

    requestPromisesRef.current.categoriesNew = promise
    return promise
  }

  const invalidateCharacteristics = () => {
    setLoadedState((current) => ({
      ...current,
      characteristicsAll: false,
      characteristicsNew: false,
    }))
  }

  const invalidateCategories = () => {
    setLoadedState((current) => ({
      ...current,
      categoriesAll: false,
      categoriesNew: false,
    }))
  }

  const refreshCharacteristics = async (options?: {
    includeModeration?: boolean
  }) => {
    const shouldRefreshModeration =
      options?.includeModeration ?? characteristicTab === 'moderation'

    invalidateCharacteristics()
    await loadCharacteristicsAll(true)
    if (shouldRefreshModeration) {
      await loadCharacteristicsNew(true)
    }
  }

  const refreshCategories = async (options?: {
    includeModeration?: boolean
  }) => {
    const shouldRefreshModeration =
      options?.includeModeration ?? categoryTab === 'moderation'

    invalidateCategories()
    await loadCategoriesAll(true)
    if (shouldRefreshModeration) {
      await loadCategoriesNew(true)
    }
  }

  useEffect(() => {
    if (!isApprover) return

    if (activeSection === 'characteristics') {
      void loadCharacteristicsAll(false, true)
      if (characteristicTab === 'moderation') {
        void loadCharacteristicsNew(false, true)
      }
      return
    }

    void loadCategoriesAll(false, true)
    void loadCategoriesNew(false, true)
  }, [activeSection, categoryTab, characteristicTab, isApprover])

  const openCategoryDetails = (record: CategoryRecord) => {
    setSelectedCategory(record)
    setCategoryDetailsOpen(true)
  }

  const openCharacteristicDetails = (record: CharacteristicRecord) => {
    setSelectedCharacteristic(record)
    setCharacteristicDetailsOpen(true)
  }

  const handleCharacteristicStatusChange = async (
    record: CharacteristicRecord,
    status: 'APPROVED' | 'REJECTED'
  ) => {
    const actionKey = `characteristic-${record.id}-${status}`
    setPendingActionKey(actionKey)

    try {
      await api.patch(`/main/characteristics/${record.id}/status`, { status })
      toast.success(
        status === 'APPROVED'
          ? 'Характеристика утверждена'
          : 'Характеристика отклонена'
      )
      await refreshCharacteristics({ includeModeration: true })
    } catch (error) {
      handleApiError(error, modal)
    } finally {
      setPendingActionKey('')
    }
  }

  const renderCharacteristicModerationActions = useCallback(
    (record: CharacteristicRecord) => (
      <Popover
        trigger="hover"
        placement="bottomRight"
        overlayClassName={actionPopoverClassName}
        content={
          <div
            className={cn.actionMenu}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              className={cn.actionMenuButton}
              onClick={() => openCharacteristicDetails(record)}
            >
              Открыть карточку
            </button>
            <button
              className={cn.actionMenuButton}
              disabled={
                pendingActionKey === `characteristic-${record.id}-APPROVED`
              }
              onClick={() =>
                void handleCharacteristicStatusChange(record, 'APPROVED')
              }
            >
              Утвердить
            </button>
            <button
              className={cn.actionMenuDangerButton}
              disabled={
                pendingActionKey === `characteristic-${record.id}-REJECTED`
              }
              onClick={() =>
                void handleCharacteristicStatusChange(record, 'REJECTED')
              }
            >
              Отклонить
            </button>
          </div>
        }
      >
        <button
          type="button"
          className={cn.actionTriggerButton}
          aria-label="Действия с характеристикой"
          onClick={(event) => event.stopPropagation()}
        >
          <MoreOutlined />
        </button>
      </Popover>
    ),
    [
      actionPopoverClassName,
      handleCharacteristicStatusChange,
      openCharacteristicDetails,
      pendingActionKey,
    ]
  )

  const handleCategoryStatusChange = async (
    record: CategoryRecord,
    status: 'APPROVED' | 'REJECTED'
  ) => {
    const actionKey = `category-${record.id}-${status}`
    setPendingActionKey(actionKey)

    try {
      await api.patch(`/categories/${record.id}/status`, { status })
      toast.success(
        status === 'APPROVED' ? 'Категория утверждена' : 'Категория отклонена'
      )
      await refreshCategories({ includeModeration: true })
    } catch (error) {
      handleApiError(error, modal)
    } finally {
      setPendingActionKey('')
    }
  }

  const handleLocalCategoryUpdate = useCallback(
    (updatedCategory: CategoryRecord) => {
      setTree(applyCategoryUpdateToTree(tree, updatedCategory))
      setSelectedCategory((current) =>
        current?.id === updatedCategory.id ? updatedCategory : current
      )
    },
    [setTree, tree]
  )

  const handleLocalCharacteristicUpdate = useCallback(
    (updated: CharacteristicRecord) => {
      setAllCharacteristics((prev) =>
        sortCharacteristics(
          prev.some((item) => item.id === updated.id)
            ? prev.map((item) => (item.id === updated.id ? updated : item))
            : [...prev, updated]
        )
      )
      setNewCharacteristics((prev) => {
        const exists = prev.some((item) => item.id === updated.id)

        if (updated.status === 'DRAFT') {
          return exists
            ? prev.map((item) => (item.id === updated.id ? updated : item))
            : [...prev, updated].sort((a, b) => a.code - b.code)
        }

        return exists ? prev.filter((item) => item.id !== updated.id) : prev
      })
      setSelectedCharacteristic((current) =>
        current?.id === updated.id ? updated : current
      )
    },
    []
  )

  const moderationCharacteristicColumns = useMemo<
    CatalogApprovalColumn<CharacteristicRecord>[]
  >(
    () => [
      {
        id: 'code',
        accessorKey: 'code',
        header: 'Код',
        cell: (info) => info.getValue<number>(),
        width: '120px',
      },
      {
        id: 'description',
        accessorKey: 'description',
        header: 'Наименование',
        cell: (info) => info.getValue<string>(),
        width: 'minmax(260px, 2fr)',
      },
      {
        id: 'unitOfMeasurement',
        header: 'Единица измерения',
        cell: ({ row }) =>
          row.original.unitOfMeasurementDto?.description || '—',
        width: '220px',
      },
      {
        id: 'valueType',
        accessorKey: 'valueType',
        header: 'Тип значений',
        cell: ({ row }) =>
          TYPE_LABELS[row.original.valueType] ?? row.original.valueType,
        width: '160px',
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: 'Статус',
        cell: ({ row }) => <StatusChip status={row.original.status} />,
        width: '190px',
      },
      {
        id: 'actions',
        header: 'Действия',
        cell: ({ row }) => renderCharacteristicModerationActions(row.original),
        width: '96px',
      },
    ],
    [renderCharacteristicModerationActions]
  )

  const allCharacteristicColumns = useMemo<
    CatalogApprovalColumn<CharacteristicRecord>[]
  >(
    () => [
      {
        id: 'code',
        accessorKey: 'code',
        header: 'Код',
        cell: (info) => info.getValue<number>(),
        width: '120px',
      },
      {
        id: 'description',
        accessorKey: 'description',
        header: 'Наименование',
        cell: (info) => info.getValue<string>(),
        width: 'minmax(260px, 2fr)',
      },
      {
        id: 'unitOfMeasurement',
        header: 'Единица измерения',
        cell: ({ row }) =>
          row.original.unitOfMeasurementDto?.description || '—',
        width: '220px',
      },
      {
        id: 'valueType',
        accessorKey: 'valueType',
        header: 'Тип значений',
        cell: ({ row }) =>
          TYPE_LABELS[row.original.valueType] ?? row.original.valueType,
        width: '160px',
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: 'Статус',
        cell: ({ row }) => <StatusChip status={row.original.status} />,
        width: '190px',
      },
      {
        id: 'actions',
        header: 'Действие',
        cell: ({ row }) => (
          <button
            className={cn.rowActionButton}
            onClick={(event) => {
              event.stopPropagation()
              openCharacteristicDetails(row.original)
            }}
          >
            Открыть карточку
          </button>
        ),
        width: '200px',
      },
    ],
    []
  )

  const normalizedSearchQuery = searchQuery.trim().toLowerCase()

  useEffect(() => {
    if (!tree.length) {
      setAntdCategoriesTree([])
      return
    }

    setAntdCategoriesTree(
      convertToAntdFormatTree({
        nodes: tree,
        classNames: cn,
        allSelectable: true,
        inTreeSelect: true,
        respectApprovalStatus: false,
      })
    )
  }, [tree])

  const filterCharacteristics = useCallback(
    (items: CharacteristicRecord[]) => {
      if (!normalizedSearchQuery) return items

      return items.filter((item) => {
        const searchable = [
          item.code,
          item.description,
          item.unitOfMeasurementDto?.description || '',
          TYPE_LABELS[item.valueType] ?? item.valueType,
        ]
          .join(' ')
          .toLowerCase()

        return searchable.includes(normalizedSearchQuery)
      })
    },
    [normalizedSearchQuery]
  )

  const filteredAllCharacteristics = useMemo(
    () =>
      filterCharacteristics(
        allCharacteristics.filter((item) => item.status === 'APPROVED')
      ),
    [allCharacteristics, filterCharacteristics]
  )
  const filteredNewCharacteristics = useMemo(
    () => filterCharacteristics(newCharacteristics),
    [filterCharacteristics, newCharacteristics]
  )

  const moderationCategoryIds = useMemo(
    () => new Set(newCategories.map((item) => item.id)),
    [newCategories]
  )

  const categoryModerationRootCounts = useMemo(() => {
    const counts = new Map<string, number>()

    if (!tree.length || moderationCategoryIds.size === 0) {
      return counts
    }

    const countModerationInSubtree = (node: CategoryInterface): number => {
      const ownCount = moderationCategoryIds.has(node.category.id) ? 1 : 0
      const childrenCount = (node.childCategories || []).reduce(
        (sum, childNode) => sum + countModerationInSubtree(childNode),
        0
      )

      return ownCount + childrenCount
    }

    tree.forEach((rootNode) => {
      const moderationCount = countModerationInSubtree(rootNode)
      if (moderationCount > 0) {
        counts.set(rootNode.category.id, moderationCount)
      }
    })

    return counts
  }, [moderationCategoryIds, tree])

  const renderCharacteristicMobileCard = (
    record: CharacteristicRecord,
    showModerationActions: boolean
  ) => (
    <div className={cn.mobileCard}>
      <div className={cn.mobileCardHeader}>
        <div>
          <div className={cn.mobileCardCode}>Код {record.code}</div>
          <div className={cn.mobileCardTitle}>{record.description}</div>
        </div>
        {getStatusBadge(record.status) ?? (
          <span className={cn.mobileCardLabel}>Утверждена</span>
        )}
      </div>

      <div className={cn.mobileCardMeta}>
        <div className={cn.mobileCardRow}>
          <span className={cn.mobileCardLabel}>Единица измерения</span>
          <div className={cn.mobileCardValue}>
            {record.unitOfMeasurementDto?.description || '—'}
          </div>
        </div>
        <div className={cn.mobileCardRow}>
          <span className={cn.mobileCardLabel}>Тип значений</span>
          <div className={cn.mobileCardValue}>
            {TYPE_LABELS[record.valueType] ?? record.valueType}
          </div>
        </div>
        <div className={cn.mobileCardRow}>
          <span className={cn.mobileCardLabel}>Статус</span>
          <div className={cn.mobileCardValue}>
            {getStatusBadge(record.status) ?? 'Утверждена'}
          </div>
        </div>
      </div>

      <div className={cn.mobileCardActions}>
        <button
          className={cn.rowActionButton}
          onClick={(event) => {
            event.stopPropagation()
            openCharacteristicDetails(record)
          }}
        >
          Открыть карточку
        </button>
        {showModerationActions && (
          <>
            <button
              className={cn.rowActionButton}
              disabled={
                pendingActionKey === `characteristic-${record.id}-APPROVED`
              }
              onClick={(event) => {
                event.stopPropagation()
                void handleCharacteristicStatusChange(record, 'APPROVED')
              }}
            >
              Утвердить
            </button>
            <button
              className={cn.rowDangerActionButton}
              disabled={
                pendingActionKey === `characteristic-${record.id}-REJECTED`
              }
              onClick={(event) => {
                event.stopPropagation()
                void handleCharacteristicStatusChange(record, 'REJECTED')
              }}
            >
              Отклонить
            </button>
          </>
        )}
      </div>
    </div>
  )

  const categoryTableConfig = useCategoryModerationTable({
    tree,
    moderationIds:
      categoryTab === 'moderation' ? moderationCategoryIds : undefined,
    approvedOnly: categoryTab === 'approved',
    rootModerationCounts: categoryModerationRootCounts,
    searchQuery,
    pendingActionKey,
    showModerationActions: categoryTab === 'moderation',
    onOpen: openCategoryDetails,
    onApprove: (record) => void handleCategoryStatusChange(record, 'APPROVED'),
    onReject: (record) => void handleCategoryStatusChange(record, 'REJECTED'),
  })

  const characteristicsTableConfig = useMemo(
    () =>
      characteristicTab === 'approved'
        ? {
            rowKey: (record: CharacteristicRecord) => record.id,
            columns: allCharacteristicColumns,
            data: filteredAllCharacteristics,
            estimateRowHeight: 68,
            renderMobileCard: (record: CharacteristicRecord) =>
              renderCharacteristicMobileCard(record, false),
          }
        : {
            rowKey: (record: CharacteristicRecord) => record.id,
            columns: moderationCharacteristicColumns,
            data: filteredNewCharacteristics,
            estimateRowHeight: 76,
            renderMobileCard: (record: CharacteristicRecord) =>
              renderCharacteristicMobileCard(record, true),
          },
    [
      characteristicTab,
      allCharacteristicColumns,
      moderationCharacteristicColumns,
      filteredAllCharacteristics,
      filteredNewCharacteristics,
      renderCharacteristicMobileCard,
    ]
  )

  if (!isApprover) {
    return <Navigate to="/" replace />
  }

  return (
    <section
      className={classNames(
        cn.catalogApproval,
        isOrganisationSelected && cn.organisationSelected
      )}
    >
      {contextHolder}

      <TreeEntityCreationModal
        open={openCreateCategoryModal}
        setOpen={setOpenCreateCategoryModal}
        externalTree={tree}
        setExternalTree={setTree}
        treeType="categories"
        allowUnapprovedSelection
        onSuccess={() => {
          void refreshCategories()
        }}
      />

      <CharacteristicCreationModal
        open={openCreateCharacteristicModal}
        setOpen={setOpenCreateCharacteristicModal}
        onSuccess={(created) => {
          if (created) {
            handleLocalCharacteristicUpdate(created)
          } else {
            void refreshCharacteristics()
          }
        }}
      />

      <CategoryDetailsModal
        open={categoryDetailsOpen}
        item={selectedCategory}
        onClose={() => {
          setCategoryDetailsOpen(false)
          setSelectedCategory(null)
        }}
        onChanged={refreshCategories}
        onLocalUpdate={handleLocalCategoryUpdate}
        categoriesTree={tree}
        antdCategoriesTree={antdCategoriesTree}
        isApprover={isApprover}
      />

      <CharacteristicDetailsModal
        open={characteristicDetailsOpen}
        item={selectedCharacteristic}
        onClose={() => {
          setCharacteristicDetailsOpen(false)
          setSelectedCharacteristic(null)
        }}
        onChanged={refreshCharacteristics}
        onLocalUpdate={handleLocalCharacteristicUpdate}
        isApprover={isApprover}
      />

      <div className={cn.inner}>
        <div className={cn.header}>
          <AtomSwitch
            options={SECTION_OPTIONS}
            value={activeSection}
            onChange={(section) => navigate(SECTION_PATHS[section])}
            ariaLabel="Справочник"
            isOrganisationSelected={isOrganisationSelected}
          />
          <div className={cn.toolbar}>
            <AtomSearchInput
              className={cn.search}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={
                activeSection === 'categories'
                  ? 'Поиск по коду или наименованию категории'
                  : 'Поиск по коду, наименованию, типу или единице измерения'
              }
              aria-label="Поиск"
              isOrganisationSelected={isOrganisationSelected}
            />
            <div className={cn.statusSelect}>
              <HoverElement>
                <AtomSelect
                  openByHover={false}
                  outlined
                  value={
                    activeSection === 'categories'
                      ? categoryTab
                      : characteristicTab
                  }
                  options={STATUS_FILTER_OPTIONS}
                  handleChange={(status) => {
                    if (!isStatusFilter(status)) return
                    if (activeSection === 'categories') setCategoryTab(status)
                    else setCharacteristicTab(status)
                  }}
                  isOrganisationSelected={isOrganisationSelected}
                />
              </HoverElement>
            </div>
            <AtomButton
              className={cn.addButton}
              icon={<PlusOutlined />}
              onClick={() =>
                activeSection === 'categories'
                  ? setOpenCreateCategoryModal(true)
                  : setOpenCreateCharacteristicModal(true)
              }
            >
              {activeSection === 'categories'
                ? 'Добавить категорию'
                : 'Добавить характеристику'}
            </AtomButton>
          </div>
        </div>

        <div className={cn.tableWrap}>
          {activeSection === 'categories' ? (
            <CatalogApprovalTable
              ariaLabel="Категории"
              isOrganisationSelected={isOrganisationSelected}
              {...categoryTableConfig}
            />
          ) : (
            <CatalogApprovalTable
              ariaLabel="Характеристики"
              isOrganisationSelected={isOrganisationSelected}
              {...characteristicsTableConfig}
            />
          )}
          {(loadingError || !isActiveSectionReady) && (
            <div className={cn.tableWrapOverlay}>
              {loadingError ? (
                <p className={cn.errorMessage}>{loadingError}</p>
              ) : (
                <Loader loaderType="common" paddingTop={64} />
              )}
            </div>
          )}
        </div>
      </div>

      <Loader loaderType="modal" isModalOpen={loading} />
    </section>
  )
}

export default CatalogApproval
